'use client';

import { useMergeRefs } from '@floating-ui/react';
import { Fragment, type MouseEventHandler, forwardRef, useCallback, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { ComboboxRootContext } from './context/combobox-root.context';
import { useComboboxChipsAndOverflow } from './hooks/use-combobox-chips-and-overflow';
import { useComboboxTriggerRenderProps } from './hooks/use-combobox-renders';
import { comboboxTriggerRecipe } from './styles/combobox-trigger.styles';
import type { ComboboxTriggerPropsInterface } from './types/combobox-trigger.types';

/**
 * @since 10.0.0
 */
export const ComboboxTrigger = forwardRef<HTMLDivElement, ComboboxTriggerPropsInterface>(
  (props: ComboboxTriggerPropsInterface, forwardedRef) => {
    const { appearance, combobox, editable, disabled, error, readOnly, size } =
      useContext(ComboboxRootContext);

    const {
      renderAdornment,
      renderChip,
      renderChipsOverflow,
      renderClear,
      renderInput,
      renderInputGroup,
      ...nonRenderProps
    } = useComboboxTriggerRenderProps(props);

    /*
     * Pull aria attributes that describe the form control out of the props
     * spread onto the trigger wrapper so they land on the underlying <input>
     * instead. The wrapper has tabIndex={-1} and is not the ARIA control.
     */
    const {
      className,
      css: cssProp,
      onClick,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledby,
      'aria-describedby': ariaDescribedby,
      ...rest
    } = nonRenderProps;

    /*
     * Forward only the aria attributes that were actually provided, so a
     * `undefined` from this layer does not overwrite the input's
     * FormField-derived defaults (e.g. aria-describedby from
     * FormFieldContext.descriptionId).
     */
    const inputAriaProps = {
      ...(ariaLabel !== undefined && { 'aria-label': ariaLabel }),
      ...(ariaLabelledby !== undefined && { 'aria-labelledby': ariaLabelledby }),
      ...(ariaDescribedby !== undefined && { 'aria-describedby': ariaDescribedby }),
    };

    const styles = css(comboboxTriggerRecipe.raw({ disabled, error, readOnly, size }), cssProp);

    const chipsAndOverflow = useComboboxChipsAndOverflow({
      renderChip,
      renderChipsOverflow,
    });

    const mergedRefs = useMergeRefs([combobox.refs.setPositionReference, forwardedRef]);

    const onClickCallback = useCallback<MouseEventHandler<HTMLDivElement>>(
      (event) => {
        combobox.onClickTrigger(event);
        onClick?.(event);
      },
      [combobox, onClick],
    );

    return (
      // oxlint-disable-next-line jsx-a11y/click-events-have-key-events
      <div
        ref={mergedRefs}
        data-c11n-component="Combobox.Trigger"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', 'c11n_combobox__trigger', styles, className)}
        onClick={disabled || readOnly ? (event) => event.preventDefault() : onClickCallback}
        onMouseDown={(event) => {
          /**
           * Prevent default to avoid focus shifts when clicking other parts of the trigger,
           * but allow default behavior on input elements to enable text selection with mouse.
           */
          if ((event.target as HTMLElement).tagName !== 'INPUT') {
            event.preventDefault();
          }
        }}
        tabIndex={-1}
        {...rest}
      >
        {renderInputGroup({
          children: (
            <Fragment>
              {chipsAndOverflow}
              {renderInput(inputAriaProps)}
            </Fragment>
          ),
        })}
        {disabled || readOnly || !editable || !combobox.selectedValues.length
          ? null
          : renderClear({})}
        {appearance === 'select' && !readOnly ? renderAdornment({}) : null}
      </div>
    );
  },
);

ComboboxTrigger.displayName = 'Combobox.Trigger';
